import type {
  CookingStep,
  CookTime,
  DayGroup,
  Duration,
  Method,
  ScheduleEntry,
  ScheduleResult,
  ServingTier,
  StepOption,
} from './types';

export function parseTime(time: string): number {
  const [h, m] = time.split(':').map(Number);
  return h * 60 + m;
}

function durationToMinutes(d?: Duration): number {
  if (!d) return 0;
  return (d.days ?? 0) * 1440 + (d.hours ?? 0) * 60 + (d.minutes ?? 0);
}

export function resolveRestMinutes(
  tier: ServingTier,
  cookTime: CookTime | undefined,
): { min: number; max?: number } {
  const min = cookTime?.restMinutes ?? tier.restMinutes ?? 0;
  const max = cookTime?.restMinutesMax ?? tier.restMinutesMax;
  return { min, max };
}

function resolveLinkedVariable(
  variable: 'thawDays' | 'cookTime' | 'restMinutes',
  tier: ServingTier,
  cookTime: CookTime | undefined,
): { min: number; max?: number } {
  switch (variable) {
    case 'thawDays':
      return { min: (tier.thawDays ?? 0) * 1440 };
    case 'cookTime':
      return cookTime
        ? { min: cookTime.cookMinutesMin, max: cookTime.cookMinutesMax }
        : { min: 0 };
    case 'restMinutes':
      return resolveRestMinutes(tier, cookTime);
    default:
      return { min: 0 };
  }
}

function resolveStepDuration(
  duration: Duration | undefined,
  durationMax: Duration | undefined,
  linkedVariable: 'thawDays' | 'cookTime' | 'restMinutes' | undefined,
  tier: ServingTier,
  cookTime: CookTime | undefined,
): { min: number; max?: number } {
  if (linkedVariable) {
    return resolveLinkedVariable(linkedVariable, tier, cookTime);
  }
  return {
    min: durationToMinutes(duration),
    max: durationMax ? durationToMinutes(durationMax) || undefined : undefined,
  };
}

export function resolveActiveCookTime(
  method: Method,
  tierKey: string,
  tier: ServingTier,
): CookTime | undefined {
  const explicit = method.cookTimes?.find((ct) => ct.tier === tierKey);
  if (explicit) return explicit;
  if (!method.minutesPerPound) return undefined;
  const rawMinutes = method.minutesPerPound * tier.weight.pounds;
  const cookMinutesMin = Math.round(rawMinutes * 2) / 2;
  return { tier: tierKey, cookMinutesMin };
}

interface StepConfig {
  label: string;
  duration: Duration | undefined;
  durationMax: Duration | undefined;
  linkedVariable: 'thawDays' | 'cookTime' | 'restMinutes' | undefined;
  daysPrior: number | undefined;
}

function resolveStepConfig(
  step: CookingStep,
  stepSelections: Record<number, number>,
  index: number,
): StepConfig | null {
  if (step.stepType === 'fixed') {
    if (!step.stepText) return null;
    return {
      label: step.stepText,
      duration: step.duration,
      durationMax: step.durationMax,
      linkedVariable: step.linkedVariable,
      daysPrior: step.daysPrior,
    };
  }

  const selectedIdx = stepSelections[index] ?? ((step.defaultOption ?? 1) - 1);
  const option: StepOption | undefined = step.options[selectedIdx];
  if (!option?.stepText) return null;
  return {
    label: option.stepText,
    duration: option.duration,
    durationMax: option.durationMax,
    linkedVariable: option.linkedVariable,
    daysPrior: option.daysPrior,
  };
}

export function buildSchedule(
  steps: Array<CookingStep>,
  stepSelections: Record<number, number>,
  tier: ServingTier,
  cookTime: CookTime | undefined,
  serveTimeStr: string,
): ScheduleResult {
  const serveMinutes = parseTime(serveTimeStr);
  let cursor = serveMinutes;
  const entries: Array<ScheduleEntry> = [];

  for (let i = steps.length - 1; i >= 0; i--) {
    const config = resolveStepConfig(steps[i], stepSelections, i);
    if (!config) continue;

    const { label, duration, durationMax, linkedVariable, daysPrior } = config;
    const { min, max } = resolveStepDuration(duration, durationMax, linkedVariable, tier, cookTime);
    if (min <= 0 && daysPrior == null) continue;

    const daysPriorMinutes = daysPrior ? daysPrior * 1440 : 0;
    const cursorSubtract = Math.max(daysPriorMinutes, (max && max > 0) ? max : min, min);
    cursor -= cursorSubtract;

    const dayOffset = daysPrior != null && daysPrior > 0
      ? -daysPrior
      : Math.floor(cursor / 1440);
    const timeInDay = ((cursor % 1440) + 1440) % 1440;

    entries.unshift({
      label,
      startMinutes: daysPrior ? 0 : timeInDay,
      durationMinutes: min > 0 ? min : 0,
      durationMaxMinutes: max,
      dayOffset,
    });
  }

  return { entries, dayGroups: groupByDay(entries) };
}

function groupByDay(entries: Array<ScheduleEntry>): Array<DayGroup> {
  const groups: Array<DayGroup> = [];
  for (const entry of entries) {
    const last = groups[groups.length - 1];
    if (last && last.dayOffset === entry.dayOffset) {
      last.entries.push(entry);
    } else {
      groups.push({ dayOffset: entry.dayOffset, entries: [entry] });
    }
  }
  return groups;
}

export function formatTime(minutesSinceMidnight: number): string {
  const totalMins = ((minutesSinceMidnight % 1440) + 1440) % 1440;
  const h = Math.floor(totalMins / 60);
  const m = totalMins % 60;
  const period = h >= 12 ? 'PM' : 'AM';
  const hour12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
  return m === 0 ? `${hour12}:00${period}` : `${hour12}:${String(m).padStart(2, '0')}${period}`;
}

export function formatHHMM(minutes: number): string {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

export function generateTimeOptions(stepMinutes: number, minTime?: string, maxTime?: string): Array<string> {
  if (stepMinutes <= 0) return [];
  const min = minTime ? parseTime(minTime) : 0;
  const max = maxTime ? parseTime(maxTime) : 1440 - stepMinutes;
  const options: Array<string> = [];
  for (let m = min; m <= max; m += stepMinutes) {
    options.push(formatHHMM(m));
  }
  return options;
}

export function formatDurationRange(
  min: number,
  max?: number,
  labels?: { minuteLabel?: string; hourLabel?: string; dayLabel?: string; daysLabel?: string },
): string {
  const fmt = (m: number): string => {
    if (m >= 1440) {
      const days = Math.round(m / 1440);
      return days !== 1
        ? `${days} ${labels?.daysLabel ?? 'days'}`
        : `${days} ${labels?.dayLabel ?? 'day'}`;
    }
    if (m >= 60) {
      const hrs = Math.round((m / 60) * 2) / 2;
      return `${hrs} ${labels?.hourLabel ?? 'hr'}`;
    }
    return `${m} ${labels?.minuteLabel ?? 'min'}`;
  };

  if (!max || max === min) return fmt(min);
  return `${fmt(min)} - ${fmt(max)}`;
}

export function formatDayOffset(
  offset: number,
  labels?: { dayOfLabel?: string; dayPriorSingularLabel?: string; dayPriorPluralLabel?: string },
): string {
  if (offset === 0) return labels?.dayOfLabel ?? 'Day of';
  const days = Math.abs(offset);
  const template = days === 1
    ? (labels?.dayPriorSingularLabel ?? '{n} day prior')
    : (labels?.dayPriorPluralLabel ?? '{n} days prior');
  return template.replace('{n}', String(days));
}

export function formatTemp(tempF: number, tempFMax?: number, labels?: { tempUnitLabel?: string }): string {
  const unit = labels?.tempUnitLabel ?? '\u00B0F';
  if (!tempFMax || tempFMax === tempF) return `${tempF}${unit}`;
  return `${tempF}-${tempFMax}${unit}`;
}

export function getWeightLabel(tier: ServingTier): string {
  if (tier.weight.displayLabel) return tier.weight.displayLabel;
  const lbs = tier.weight.pounds;
  const count = tier.weight.count ?? 1;
  if (count > 1) return `${count} x ${lbs} lb`;
  return `${lbs} lb`;
}
